import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { cloneDeep } from 'lodash-es';
import { Observable, scan, Subject, switchMap } from 'rxjs';
import { map, shareReplay, startWith } from 'rxjs/operators';

import { UserService } from '@core/api/api';
import {
  CARDS_WITH_PIN,
  CARDS_WITH_TIME_FILTER,
  DashboardCards,
  DashboardCardSettings,
  DashboardSettings,
  DashboardTimeFilterRange,
  TeamManagementMode,
  UserProfileSettings,
} from '@core/api/model';

type DashboardSettingsChange =
  | {
      type: 'timeFilter';
      data: {
        value: DashboardTimeFilterRange;
        id?: keyof DashboardCards;
      };
    }
  | {
      type: 'pin';
      data: {
        value: boolean | null;
        ids?: (keyof DashboardCards)[];
      };
    }
  | {
      type: 'dashboardSorting';
      data: (keyof DashboardCards)[];
    };

type UserProfileSettingsChangeEvent =
  | { type: 'dashboard'; change: DashboardSettingsChange }
  | { type: 'teamManagementMode'; change: TeamManagementMode }
  | { type: 'tutorial'; change: boolean };

@Injectable({
  providedIn: 'root',
})
export class UserProfileSettingsService {
  private userService = inject(UserService);

  public settings$ = this.userService.getUserProfileSettings().pipe(
    map((res) => {
      return Object.fromEntries(Object.entries(res.data ?? {}).filter(([_, v]) => v !== null));
    }),
    map((settings: Partial<UserProfileSettings>) => ({ ...this.initialSettings, ...settings })),
    switchMap((initialSettings: UserProfileSettings) => {
      return this.changeSettings$.pipe(
        scan(
          (currentSettings, event) => this.applySettingsChange(event, currentSettings),
          initialSettings,
        ),
        switchMap((settings) =>
          this.userService.saveUserProfileSettings(settings).pipe(map(() => settings)),
        ),
        startWith(initialSettings),
      );
    }),
    takeUntilDestroyed(),
    shareReplay(1),
  );

  public dashboardSettings$: Observable<DashboardSettings> = this.settings$.pipe(
    map((settings) => settings.dashboard!),
  );

  public showInitialTutorials$: Observable<boolean> = this.settings$.pipe(
    map((settings) => settings.showInitialTutorials as boolean),
  );

  public teamManagementMode$: Observable<TeamManagementMode | null> = this.settings$.pipe(
    map((settings) => settings.teamManagementMode),
  );

  private readonly initialDashboardSettings: DashboardSettings = {
    atLab: { timeFilter: DashboardTimeFilterRange.WEEK_2, alwaysVisible: true },
    inDraft: { timeFilter: DashboardTimeFilterRange.WEEK_2, alwaysVisible: true },
    exceededLimit: { timeFilter: DashboardTimeFilterRange.WEEK_2, alwaysVisible: true },
    finished: { timeFilter: DashboardTimeFilterRange.WEEK_2, alwaysVisible: true },
    invoices: { timeFilter: DashboardTimeFilterRange.WEEK_2, alwaysVisible: true },
    progress: { timeFilter: DashboardTimeFilterRange.WEEK_2, alwaysVisible: true },
    sent: { timeFilter: DashboardTimeFilterRange.WEEK_2, alwaysVisible: true },
    messages: { alwaysVisible: true },
    ftp: { alwaysVisible: true },
    sorting: [
      'atLab',
      'inDraft',
      'sent',
      'exceededLimit',
      'finished',
      'invoices',
      'progress',
    ] as (keyof DashboardCards)[],
  };

  private readonly initialSettings: UserProfileSettings = {
    dashboard: this.initialDashboardSettings,
    teamManagementMode: null,
    showInitialTutorials: true,
  };

  private readonly changeSettings$: Subject<UserProfileSettingsChangeEvent> =
    new Subject<UserProfileSettingsChangeEvent>();

  public saveDashboardSettings(timeFilter: DashboardTimeFilterRange, id?: keyof DashboardCards) {
    this.changeSettings$.next({
      type: 'dashboard',
      change: { type: 'timeFilter', data: { value: timeFilter, id } },
    });
  }

  public saveDashboardPinSettings(pinned: boolean | null, ids?: (keyof DashboardCards)[]) {
    this.changeSettings$.next({
      type: 'dashboard',
      change: { type: 'pin', data: { value: pinned, ids } },
    });
  }

  public saveDashboardSortingSettings(sorting: (keyof DashboardCards)[]) {
    this.changeSettings$.next({
      type: 'dashboard',
      change: { type: 'dashboardSorting', data: sorting },
    });
  }

  public saveTeamManagementMode(mode: TeamManagementMode) {
    this.changeSettings$.next({ type: 'teamManagementMode', change: mode });
  }

  public saveTutorialSettings(showInitialTutorials: boolean) {
    this.changeSettings$.next({
      type: 'tutorial',
      change: showInitialTutorials,
    });
  }

  private applySettingsChange(
    event: UserProfileSettingsChangeEvent,
    currentSettings: UserProfileSettings,
  ): UserProfileSettings {
    switch (event.type) {
      case 'dashboard':
        currentSettings.dashboard = this.applyDashboardChange(
          event.change,
          currentSettings.dashboard ?? this.initialDashboardSettings,
        );
        break;
      case 'teamManagementMode':
        currentSettings.teamManagementMode = event.change;
        break;
      case 'tutorial': {
        currentSettings.showInitialTutorials = event.change;
        break;
      }
    }
    return currentSettings;
  }

  private applyDashboardChange(
    changeDashboardSettings: DashboardSettingsChange,
    currentSettings: DashboardSettings,
  ): DashboardSettings {
    const newSettings = cloneDeep(currentSettings);
    if (changeDashboardSettings.type === 'timeFilter') {
      const timeFilter = changeDashboardSettings.data;
      if (timeFilter.id) {
        const currentDashboardCardSetting: DashboardCardSettings | null | undefined =
          newSettings[timeFilter.id];
        if (currentDashboardCardSetting) {
          currentDashboardCardSetting.timeFilter = timeFilter.value;
        } else {
          newSettings[timeFilter.id] = {
            timeFilter: timeFilter.value,
            alwaysVisible: null,
          };
        }
      } else {
        CARDS_WITH_TIME_FILTER.forEach((key) => {
          const setting = (newSettings[key] ??= this.initialDashboardSettings[key]);
          if (setting !== null) {
            setting.timeFilter = timeFilter.value;
          }
        });
      }
    } else if (changeDashboardSettings.type === 'dashboardSorting') {
      newSettings.sorting = changeDashboardSettings.data;
    } else if (changeDashboardSettings.type === 'pin') {
      const pin = changeDashboardSettings.data;
      if (pin.ids) {
        pin.ids.forEach((id) => {
          const currentDashboardCardSetting: DashboardCardSettings | null | undefined =
            newSettings[id];
          if (currentDashboardCardSetting) {
            currentDashboardCardSetting.alwaysVisible = pin.value;
          } else {
            newSettings[id] = {
              alwaysVisible: pin.value,
            };
          }
        });
      } else {
        CARDS_WITH_PIN.map((key) => (newSettings[key] ??= this.initialDashboardSettings[key]))
          .filter((setting): setting is DashboardCardSettings => !!setting)
          .forEach((setting) => {
            setting.alwaysVisible = pin.value;
          });
      }
    }
    return newSettings;
  }
}
